import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, ApiError, type PlatformWorkspace } from "../../api";
import { useDialogs } from "../../components/Dialog";
import { dateTime } from "../../lib/format";
import { useI18n, type Dict } from "../../lib/i18n";
import { errorCode, type PlanName } from "../../lib/plan";
import { apiErrorText } from "../../lib/apiError";

/**
 * Every workspace on the platform, with the knobs the owner of the platform
 * needs while there is no self sign-up: opening a customer, their plan,
 * suspension, and — for the account that has to go — deletion.
 *
 * Platform-admin only (NavRail's navKeys, route.ts's effectiveRoute, and the
 * server's requirePlatformAdmin, which answers 404 to everyone else).
 *
 * A manual change to a workspace Stripe is already billing is allowed but
 * warned about: the next subscription event re-applies Stripe's own state and
 * would quietly undo it.
 */

/** What can be assigned from here. "unlimited" is the platform owner's own and is only ever shown, never offered. */
const PLANS: readonly PlanName[] = ["free", "starter", "team"];
/** How many rows are drawn at first, and added with each "show more". */
const PAGE = 50;

function failureMessage(t: Dict, err: unknown): string {
  switch (errorCode(err)) {
    case "smtp_required":
      return t.customers.errSmtp;
    case "no_owner":
      return t.customers.errNoOwner;
    case "already_active":
      return t.customers.errAlreadyActive;
    case "mail_failed":
      return t.customers.errMailFailed;
    case "already_operator":
      return t.customers.errAlreadyOperator;
    case "cannot_delete_own_workspace":
      return t.customers.errOwnWorkspace;
    case "has_subscription":
      return t.customers.errHasSubscription;
    default:
      return apiErrorText(err, t, t.customers.updateFailed);
  }
}

/** The rows a search keeps: the name, the address or the owner, in any case. */
export function matchWorkspaces<T extends { name: string; slug: string; owner: { email: string; displayName: string } | null }>(
  rows: T[],
  search: string,
): T[] {
  const q = search.trim().toLocaleLowerCase("tr");
  if (!q) return rows;
  return rows.filter((row) =>
    [row.name, row.slug, row.owner?.email ?? "", row.owner?.displayName ?? ""].some((field) =>
      field.toLocaleLowerCase("tr").includes(q),
    ),
  );
}

type Draft = { plan: PlanName };
const EMPTY_NEW = { workspaceName: "", displayName: "", email: "", plan: "free" as PlanName };

export function CustomersView(): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.customers;
  const dialogs = useDialogs();
  const [rows, setRows] = useState<PlatformWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [notes, setNotes] = useState<Record<number, { tone: "ok" | "error"; text: string }>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [adding, setAdding] = useState(false);
  const [fresh, setFresh] = useState(EMPTY_NEW);
  const [creating, setCreating] = useState(false);
  const [banner, setBanner] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.platformWorkspaces();
      setRows(res.workspaces);
      setDrafts({});
      setError(null);
    } catch {
      setError(t.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [t.loadFailed]);

  useEffect(() => {
    void load();
  }, [load]);

  const matching = useMemo(() => matchWorkspaces(rows, search), [rows, search]);
  const visible = matching.slice(0, shown);

  const draftOf = (row: PlatformWorkspace): Draft => drafts[row.id] ?? { plan: row.storedPlan };

  const note = (id: number, tone: "ok" | "error", text: string): void =>
    setNotes((current) => ({ ...current, [id]: { tone, text } }));

  async function patch(row: PlatformWorkspace, body: Parameters<typeof api.updatePlatformWorkspace>[1]): Promise<void> {
    setBusy(row.id);
    try {
      const res = await api.updatePlatformWorkspace(row.id, body);
      setRows((current) => current.map((r) => (r.id === row.id ? res.workspace : r)));
      setDrafts(({ [row.id]: _drop, ...rest }) => rest);
      const paused = res.paused.sites.length + res.paused.operators.length;
      const resumed = res.resumed.sites.length + res.resumed.operators.length;
      const extra = paused
        ? ` ${t.pausedNote(res.paused.sites.length, res.paused.operators.length)}`
        : resumed
          ? ` ${t.resumedNote(res.resumed.sites.length, res.resumed.operators.length)}`
          : "";
      note(row.id, "ok", t.saved(res.workspace.name) + extra);
    } catch (err) {
      note(row.id, "error", failureMessage(all, err));
    } finally {
      setBusy(null);
    }
  }

  async function resend(row: PlatformWorkspace): Promise<void> {
    setBusy(row.id);
    try {
      const res = await api.resendInvite(row.id);
      note(row.id, "ok", t.resent(res.email));
    } catch (err) {
      note(row.id, "error", failureMessage(all, err));
    } finally {
      setBusy(null);
    }
  }

  async function suspend(row: PlatformWorkspace, suspended: boolean): Promise<void> {
    if (!suspended) {
      const sure = await dialogs.confirm({ title: t.confirmSuspend(row.name), confirmLabel: t.suspend, danger: true });
      if (!sure) return;
    }
    await patch(row, { planStatus: suspended ? "active" : "suspended" });
  }

  async function remove(row: PlatformWorkspace): Promise<void> {
    const sure = await dialogs.confirm({
      title: t.deleteTitle(row.name),
      body: t.deleteBody,
      confirmLabel: t.delete,
      danger: true,
      // The slug, not the name: it is unique, and it is what the server checks.
      typeToConfirm: row.slug,
    });
    if (!sure) return;
    setBusy(row.id);
    try {
      await api.deletePlatformWorkspace(row.id, row.slug);
      setRows((current) => current.filter((r) => r.id !== row.id));
      setBanner({ tone: "ok", text: t.deleted(row.name) });
    } catch (err) {
      note(row.id, "error", failureMessage(all, err));
    } finally {
      setBusy(null);
    }
  }

  async function create(event: Event): Promise<void> {
    event.preventDefault();
    setCreating(true);
    setBanner(null);
    const email = fresh.email.trim();
    try {
      const res = await api.createPlatformWorkspace({
        workspaceName: fresh.workspaceName.trim(),
        displayName: fresh.displayName.trim(),
        email,
        plan: fresh.plan,
      });
      setRows((current) => [res.workspace, ...current]);
      setBanner({ tone: "ok", text: t.createdOk(email) });
      setFresh(EMPTY_NEW);
      setAdding(false);
    } catch (err) {
      // The account exists; only the invite failed. The list has the retry.
      if (err instanceof ApiError && err.status === 502 && errorCode(err) === "mail_failed") {
        setBanner({ tone: "error", text: t.createdMailFailed });
        setFresh(EMPTY_NEW);
        setAdding(false);
        await load();
      } else {
        setBanner({ tone: "error", text: failureMessage(all, err) });
      }
    } finally {
      setCreating(false);
    }
  }

  const canCreate = fresh.workspaceName.trim().length > 0 && fresh.displayName.trim().length >= 2 && fresh.email.trim().length > 3;

  return (
    <div class="stats-page customers">
      <header class="stats-head">
        <h1>{t.title}</h1>
        <div class="stats-filters">
          <input
            type="search"
            class="page-search"
            placeholder={t.search}
            aria-label={t.search}
            value={search}
            onInput={(e) => {
              setSearch(e.currentTarget.value);
              setShown(PAGE);
            }}
          />
          <button class="btn primary" aria-expanded={adding} onClick={() => setAdding((open) => !open)}>
            {t.newOpen}
          </button>
        </div>
      </header>
      <p class="hint" style={{ margin: "-8px 0 14px" }}>
        {t.intro}
      </p>

      {banner ? (
        <div class={`alert${banner.tone === "error" ? " error" : ""}`} role="status" style={{ margin: "0 0 14px" }}>
          {banner.text}
        </div>
      ) : null}

      {adding ? (
        <form class="card new-customer" onSubmit={(e) => void create(e)}>
          <h3>{t.newTitle}</h3>
          <p class="hint" style={{ margin: "0 0 10px" }}>
            {t.newIntro}
          </p>
          <div class="new-customer-grid">
            <label>
              {t.workspaceName}
              <input
                type="text"
                maxLength={120}
                value={fresh.workspaceName}
                onInput={(e) => setFresh({ ...fresh, workspaceName: e.currentTarget.value })}
                required
              />
            </label>
            <label>
              {t.ownerName}
              <input
                type="text"
                maxLength={120}
                value={fresh.displayName}
                onInput={(e) => setFresh({ ...fresh, displayName: e.currentTarget.value })}
                required
              />
            </label>
            <label>
              {t.ownerEmail}
              <input
                type="email"
                maxLength={200}
                value={fresh.email}
                onInput={(e) => setFresh({ ...fresh, email: e.currentTarget.value })}
                required
              />
            </label>
            <label>
              {t.plan}
              <select value={fresh.plan} onChange={(e) => setFresh({ ...fresh, plan: e.currentTarget.value as PlanName })}>
                {PLANS.map((p) => (
                  <option key={p} value={p}>
                    {all.plan.names[p] ?? p}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div class="actions-row">
            <button class="btn primary" disabled={creating || !canCreate}>
              {creating ? t.creating : t.create}
            </button>
            <button type="button" class="btn" onClick={() => setAdding(false)}>
              {all.common.cancel}
            </button>
          </div>
        </form>
      ) : null}

      {error ? (
        <div class="alert error" role="alert">
          {error}
        </div>
      ) : null}

      {loading && rows.length === 0 ? (
        <p class="hint">{all.common.loading}</p>
      ) : rows.length === 0 ? (
        <p class="hint">{t.empty}</p>
      ) : matching.length === 0 ? (
        <p class="hint">{t.noMatch}</p>
      ) : (
        <div class="table-wrap">
          <table class="data-table customers">
            <thead>
              <tr>
                <th scope="col">{t.name}</th>
                <th scope="col">{t.owner}</th>
                <th scope="col">{t.plan}</th>
                <th scope="col">{t.usage}</th>
                <th scope="col">{t.created}</th>
                <th scope="col">{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const draft = draftOf(row);
                const dirty = draft.plan !== row.storedPlan;
                const suspended = row.planStatus === "suspended";
                return (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.name}</strong>
                      <span class="hint block">{row.slug}</span>
                    </td>
                    <td>
                      {row.owner ? (
                        <>
                          <a href={`mailto:${row.owner.email}`}>{row.owner.email}</a>
                          <span class="hint block">{row.owner.displayName}</span>
                        </>
                      ) : (
                        t.noOwner
                      )}
                    </td>
                    <td>
                      <select
                        aria-label={`${t.plan} — ${row.name}`}
                        value={draft.plan}
                        // The owner's own workspace: its plan is not one this list can give or take away.
                        disabled={busy === row.id || row.storedPlan === "unlimited"}
                        onChange={(e) =>
                          setDrafts((current) => ({
                            ...current,
                            [row.id]: { ...draft, plan: e.currentTarget.value as PlanName },
                          }))
                        }
                      >
                        {(row.storedPlan === "unlimited" ? (["unlimited"] as const) : PLANS).map((p) => (
                          <option key={p} value={p}>
                            {all.plan.names[p] ?? p}
                          </option>
                        ))}
                      </select>
                      <span class={`chip block${suspended ? " status-pending" : ""}`}>
                        {suspended ? all.billing.statusSuspended : row.planStatus === "past_due" ? all.billing.statusPastDue : row.planStatus === "canceled" ? all.billing.statusCanceled : all.billing.statusActive}
                      </span>
                    </td>
                    <td class="nowrap">{t.usageCell(row.usage.sites, row.usage.operators)}</td>
                    <td class="nowrap">{dateTime(row.createdAt, lang)}</td>
                    <td>
                      {row.hasSubscription ? <span class="hint block">{t.stripeWarning}</span> : null}
                      <div class="actions-row">
                        <button
                          class="btn sm primary"
                          disabled={!dirty || busy === row.id}
                          onClick={() => void patch(row, { plan: draft.plan })}
                        >
                          {t.save}
                        </button>
                        <button
                          class={`btn sm${suspended ? "" : " danger"}`}
                          disabled={busy === row.id}
                          onClick={() => void suspend(row, suspended)}
                        >
                          {suspended ? t.unsuspend : t.suspend}
                        </button>
                        <button class="btn sm" disabled={busy === row.id} onClick={() => void resend(row)}>
                          {t.resend}
                        </button>
                        <button class="btn sm danger" disabled={busy === row.id} onClick={() => void remove(row)}>
                          {t.delete}
                        </button>
                      </div>
                      {notes[row.id] ? (
                        <span class={`hint block${notes[row.id]!.tone === "error" ? " error" : ""}`} role="status">
                          {notes[row.id]!.text}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {matching.length > visible.length ? (
        <div class="actions-row" style={{ alignItems: "center" }}>
          <button class="btn" onClick={() => setShown((n) => n + PAGE)}>
            {all.common.showMore}
          </button>
          <span class="hint">{t.shown(visible.length, matching.length)}</span>
        </div>
      ) : null}
      {dialogs.dialog}
    </div>
  );
}
